import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { UserDatum } from '@app/shared/interfaces/files.interface';
import {
  InAppBrowser,
  InAppBrowserOptions,
} from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Capacitor } from '@capacitor/core';
import { environment } from '@env';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../shared/shared.service';
import { ToastService } from '../toast/toast.service';
import { BLANK_URL } from '@app/core/constants/payment-constants';

declare var google: any;
declare var gapi: any;
const inAppOptions: InAppBrowserOptions = {
  location: 'no',
  hidden: 'no',
  clearcache: 'no',
  clearsessioncache: 'no',
  zoom: 'no', //Android only
  hardwareback: 'no',
  mediaPlaybackRequiresUserAction: 'no',
  shouldPauseOnSuspend: 'no', //Android only
  closebuttoncolor: '#ffffff',
  disallowoverscroll: 'no', //iOS only
  toolbar: 'no', //iOS only
  enableViewportScale: 'no', //iOS only
  allowInlineMediaPlayback: 'no', //iOS only
  presentationstyle: 'fullscreen', //iOS only
  fullscreen: 'yes', //Windows only,
  hideurlbar: 'yes',
  usewkwebview: 'yes',
  toolbarcolor: '#a9a9a9',
  toolbartranslucent: 'no',
  toolbarposition: 'top',
  hidenavigationbuttons: 'yes',
  closebuttoncaption: 'X',
  lefttoright: 'no',
  hidespinner: 'yes',
};

@Injectable({
  providedIn: 'root',
})
export class DrivePickerService {
  accessToken!: string;
  picker: any;
  private drivePickerOpen = false;
  private pickerObserver: MutationObserver | null = null;

  constructor(
    public iab: InAppBrowser,
    private sharedService: SharedService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private loadingCtrl: LoadingController,
    private router: Router,
    private ngZone: NgZone
  ) {}

  createPicker(accessToken: string) {
    if (!this.drivePickerOpen) {
      this.drivePickerOpen = true;
      this.sharedService.reset();
      this.accessToken = accessToken;
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        this.createPickerIOS();
      } else {
        this.createPickerWeb();
      }
    }
  }

  pickerInit() {
    gapi.load('picker');
  }

  createPickerIOS() {
    const allowedMimeTypes = this.sharedService.getFileMimeTypes();
    const scriptString = `
    (function injectViewport() {
      if (!document.querySelector('meta[name="viewport"]')) {
        var meta = document.createElement('meta');
        meta.name = "viewport";
        meta.content = "width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=1, user-scalable=no";
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
      var style = document.createElement('style');
      style.innerHTML = 'html, body { background-color: transparent !important; margin: 0 !important; padding: 0 !important; width: 100vw !important; height: 100vh !important; overflow: hidden !important; } .picker-dialog-bg { background-color: rgba(0,0,0,0.5) !important; }';
      document.head.appendChild(style);
    })();

    function adjustPickerSize() {
      const pickerDialog = document.querySelector('.picker-dialog');
      const pickerDialogContent = document.querySelector('.picker-dialog .picker-dialog-content');

      if (pickerDialog) {
        pickerDialog.setAttribute(
          'style',
          'width: calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 10px) !important; height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 10px) !important; max-width: calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 10px) !important; max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 10px) !important; top: calc(env(safe-area-inset-top) + 5px) !important; left: calc(env(safe-area-inset-left) + 5px) !important; border-radius: 12px !important; font-family: "Google Sans", Arial, Helvetica, sans-serif !important;'
        );
      }

      if (pickerDialogContent) {
        pickerDialogContent.setAttribute(
          'style',
          'width: 100% !important; height: 100% !important; font-family: "Google Sans", Arial, Helvetica, sans-serif !important;'
        );
      }
    }

    function disposeInternalPicker() {
      if (window.googlePicker) {
        window.googlePicker.dispose();
        window.googlePicker = null;
      }
      window.removeEventListener('resize', adjustPickerSize);
      window.removeEventListener('orientationchange', adjustPickerSize);
      if (window.pickerObserver) {
        window.pickerObserver.disconnect();
        window.pickerObserver = null;
      }
      window.isPickerInitialized = false;
    }

    if (!window.gapiScriptLoaded) {
      window.gapiScriptLoaded = true;
      var s = document.createElement('script');
      s.src = 'https://apis.google.com/js/api.js';
      s.async = true;
      s.defer = true;
      s.onload = onApiLoad;
      document.body.appendChild(s);
    } else if (!window.isPickerInitialized) {
      onApiLoad();
    }

    function onApiLoad() {
      if (window.gapi) {
        gapi.load('picker', { callback: onPickerApiLoad });
      }
    }

    function onPickerApiLoad() {
      if (window.isPickerInitialized) return;
      window.isPickerInitialized = true;

      var view = new google.picker.DocsView()
        .setIncludeFolders(false)
        .setSelectFolderEnabled(false)
        .setMimeTypes('${allowedMimeTypes}');

      window.googlePicker = new google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken('${this.accessToken}')
        .setDeveloperKey('${environment.GAPI_API_KEY}')
        .setAppId('${environment.GAPI_APP_ID}')
        .setCallback(pickerCallback)
        .enableFeature(google.picker.Feature.NAV_HIDDEN)
        .enableFeature(google.picker.Feature.FULLSCREEN)
        .build();

      window.googlePicker.setVisible(true);
      adjustPickerSize();

      if (window.pickerObserver) window.pickerObserver.disconnect();
      window.pickerObserver = new MutationObserver(adjustPickerSize);
      window.pickerObserver.observe(document.body, { childList: true, subtree: true });

      window.addEventListener('resize', adjustPickerSize);
      window.addEventListener('orientationchange', adjustPickerSize);

      var bg = document.getElementsByClassName('picker-dialog-bg')[1];
      if (bg) {
        bg.addEventListener('click', function () {
          disposeInternalPicker();
          var message = {event: 'close'};
          if (window.webkit && window.webkit.messageHandlers) {
            if (window.webkit.messageHandlers.cordova_iab) {
               window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
            } else if (window.webkit.messageHandlers.capgo_iab) {
               window.webkit.messageHandlers.capgo_iab.postMessage(JSON.stringify(message));
            }
          }
        });
      }
    }

    function pickerCallback(data) {
      if (data.action === google.picker.Action.PICKED) {
        var doc = data[google.picker.Response.DOCUMENTS][0];
        var responseData = {
          url: doc.url,
          fileId: doc.id,
          fileName: doc.name,
          mimeType: doc.mimeType,
          size: doc.sizeBytes
        };
        if (window.webkit && window.webkit.messageHandlers) {
          if (window.webkit.messageHandlers.cordova_iab) {
             window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(responseData));
          } else if (window.webkit.messageHandlers.capgo_iab) {
             window.webkit.messageHandlers.capgo_iab.postMessage(JSON.stringify(responseData));
          }
        }
        disposeInternalPicker();
      } else if (data.action === google.picker.Action.CANCEL) {
        var message = {event: 'close'};
        if (window.webkit && window.webkit.messageHandlers) {
          if (window.webkit.messageHandlers.cordova_iab) {
             window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
          } else if (window.webkit.messageHandlers.capgo_iab) {
             window.webkit.messageHandlers.capgo_iab.postMessage(JSON.stringify(message));
          }
        }
        disposeInternalPicker();
      }
    }
  `;

    const browser = this.iab.create(BLANK_URL, '_blank', inAppOptions);

    browser.on('exit').subscribe(() => {
      this.ngZone.run(() => {
        this.drivePickerOpen = false;
        this.disposePicker();
      });
    });

    browser.on('loadstop').subscribe(() => {
      browser.executeScript({ code: scriptString });
      browser.insertCSS({ file: '../../theme/style.css' });
    });

    browser.on('loaderror').subscribe((err) => {
      console.error('IAB Picker load error:', err);
      this.drivePickerOpen = false;
    });

    browser.on('message').subscribe((event: any) => {
      this.ngZone.run(() => {
        const data = event?.data;
        if (data?.event === 'close') {
          this.drivePickerOpen = false;
          browser.close();
          return;
        }

        if (data?.fileId) {
          this.drivePickerOpen = false;
          const file: UserDatum = {
            id: data.fileId,
            name: data.fileName || '',
            mimeType: data.mimeType || '',
            size: data.size || 0,
          };
          this.downloadFile(file);
        } else {
          console.warn('IAB Picker: Invalid message or unknown event', data);
        }

        browser.close();
      });
    });
  }

  createPickerWeb() {
    const allowedMimeTypes = this.sharedService.getFileMimeTypes();
    const view = new google.picker.DocsView()
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false)
      .setMimeTypes(allowedMimeTypes);
    this.picker = new google.picker.PickerBuilder()
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      .setDeveloperKey(environment.GAPI_API_KEY)
      .setAppId(environment.GAPI_APP_ID)
      .setOAuthToken(this.accessToken)
      .addView(view)
      .setCallback(this.pickerCallback.bind(this))
      .build();
    this.picker.setVisible(true);

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      this.adjustAndroidPickerStyle();
      if (this.pickerObserver) this.pickerObserver.disconnect();
      this.pickerObserver = new MutationObserver(() =>
        this.adjustAndroidPickerStyle()
      );
      this.pickerObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('orientationchange', this.handleResize);
    }

    const el = document.getElementsByClassName('picker-dialog-bg')[1];
    if (el) {
      el.addEventListener('click', () => {
        this.drivePickerOpen = false;
        this.disposePicker();
      });
    }
    
    setTimeout(() => {
      document.getElementsByClassName('picker-dialog-frame')[0].setAttribute('aria-hidden', 'true');
    }, 100);
  }

  disposePicker() {
    this.drivePickerOpen = false;
    if (this.picker) {
      this.picker.dispose();
      this.picker = null;
    }
    if (this.pickerObserver) {
      this.pickerObserver.disconnect();
      this.pickerObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
  }

  async pickerCallback(data: any) {
    if (data.action === google.picker.Action.PICKED) {
      this.drivePickerOpen = false;
      const document: any = data[google.picker.Response.DOCUMENTS][0];
      if (document) {
        const file: UserDatum = {
          id: document.id,
          name: document.name,
          mimeType: document.mimeType,
          size: document.sizeBytes,
        };
        this.downloadFile(file);
      }
    } else if (data.action == google.picker.Action.CANCEL) {
      this.drivePickerOpen = false;
      this.disposePicker();
    }
  }

  async downloadFile(file: UserDatum) {
    this.ngZone.run(async () => {
      // Validate Drive metadata before downloading the file body. This avoids
      // triggering the heavy download path for files that would be rejected anyway.
      const validate = this.sharedService.validateRemoteDescriptor({
        name: file.name,
        mimeType: file.mimeType || '',
        size: Number(file.size || 0),
      });
      if (!validate.isValid) {
        this.toastService.showErrorToast(validate.error);
        return;
      }

      const loading = await this.loadingCtrl.create({
        message: this.translateService.instant('DOWNLOADING_FILES'),
      });

      loading.present();

      try {
        // Use fetch() for the media request so Android avoids the native HTTP
        // plugin's blob/base64 buffering path that was causing OOM crashes.
        const response = await fetch(
          'https://www.googleapis.com/drive/v3/files/' + file.id + '?alt=media',
          {
            headers: { Authorization: 'Bearer ' + this.accessToken },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to download Google Drive file');
        }

        const blob = await response.blob();
        // Keep the existing queue contract intact for now by converting the
        // downloaded Blob into base64 before handing it to SharedService.
        const data = await this.sharedService.blobToBase64(blob);

        if (data) {
          await this.sharedService.addBlobToQueue(
            {
              content: data,
              contentType: file.mimeType || '',
              extension: this.sharedService.getExtension(file.name),
              fileName: file.name,
              lastModified: '',
              size: Number(file.size),
            },
            data
          );
        }
        loading.dismiss();
        if (!this.sharedService.isDomainReady()) {
          this.router.navigate(['/app/add-files/maps'], {
            state: { showMenu: false },
          });
        } else {
          this.router.navigate(['app/add-files/upload']);
        }
      } catch (error) {
        this.toastService.showErrorToast('FILE_TYPE_NOT_SUPPORTED');
        console.log('api call error ===>', error);
      } finally {
        this.disposePicker();
        loading.dismiss();
      }
    });
  }

  private handleResize = () => {
    this.adjustAndroidPickerStyle();
  };

  private adjustAndroidPickerStyle() {
    const pickerDialog = document.querySelector('.picker-dialog');
    const pickerDialogContent = document.querySelector(
      '.picker-dialog .picker-dialog-content'
    );

    if (pickerDialog) {
      pickerDialog.setAttribute(
        'style',
        `width: calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 10px) !important;
         height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 10px) !important;
         max-width: calc(100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 10px) !important;
         max-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 10px) !important;
         top: calc(env(safe-area-inset-top) + 5px) !important;
         left: calc(env(safe-area-inset-left) + 5px) !important;
         border-radius: 12px !important;
         font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;`
      );
    }

    if (pickerDialogContent) {
      pickerDialogContent.setAttribute(
        'style',
        `width: 100% !important; height: 100% !important; font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;`
      );
    }
  }
}
